import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { Building2, ArrowRight, Shield, Loader2, X } from 'lucide-react';
import './CustomLogin.css';

const CustomLogin = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const user = await getCurrentUser();
        if (user) {
          console.log('✅ User already authenticated:', user.username);
          onSuccess();
          return;
        }
      } catch (error) {
        console.log('❌ User not authenticated, showing login UI');
        
        // Check if we're returning from an OAuth redirect
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error_param = urlParams.get('error');
        
        if (error_param) {
          console.error('❌ OAuth error:', error_param);
          setError(`Authentication failed: ${error_param}`);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else if (code) {
          console.log('🔄 OAuth callback detected, letting Amplify handle it...');
          setMessage('Processing authentication...');
          // Let Amplify handle the OAuth callback naturally
          // Don't try to manually process it
        }
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuthStatus();
  }, [onSuccess]);

  // Handle EntraID sign-in using Amplify's signInWithRedirect but with custom provider
  const handleEntraIDSignIn = async () => {
    setLoading(true);
    setError('');
    setMessage('Redirecting to Microsoft sign-in...');

    try {
      console.log('🔄 Starting EntraID sign-in flow...');
      
      // Use Amplify's signInWithRedirect but specify EntraID provider directly
      // This should bypass the Cognito UI and go straight to EntraID
      await signInWithRedirect({
        provider: {
          custom: 'EntraID'
        }
      });
      
      // This won't execute as the page will redirect
      console.log('🔄 Redirecting to EntraID...');
      
    } catch (error) {
      console.error('❌ EntraID sign-in error:', error);
      setError(error.message || 'Failed to initiate Microsoft sign-in. Please try again.');
      setLoading(false);
      setMessage('');
    }
  };

  // Show loading spinner while checking auth status
  if (checkingAuth) {
    return (
      <div className="custom-login">
        <div className="login-container">
          <div className="login-card">
            <div className="auth-loading">
              <Loader2 className="loading-icon" size={40} />
              <p>Checking authentication status...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-login">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <Shield className="login-icon" size={48} />
            <h1>RSS Aggregator Admin</h1>
            <p>Sign in to access the admin panel</p>
          </div>

          {error && (
            <div className="alert error">
              {error}
            </div>
          )}

          {message && (
            <div className="alert info">
              {message}
            </div>
          )}

          <div className="auth-options">
            {/* Microsoft EntraID Sign-In */}
            <button 
              onClick={handleEntraIDSignIn} 
              disabled={loading} 
              className="auth-button primary microsoft"
            >
              {loading ? (
                <Loader2 className="loading-icon" size={20} />
              ) : (
                <>
                  <Building2 size={20} />
                  Sign in with Microsoft
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>

          {/* Cancel Button - Navigate back to homepage */}
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <button 
              onClick={() => navigate('/')} 
              className="auth-button primary cancel"
            >
              <X size={20} />
              Cancel
              <X size={20} style={{ opacity: 0 }} />
            </button>
          </div>

          {/* Information Section */}
          <div className="auth-info">
            <div className="info-item">
              <Building2 size={16} />
              <span>Use your Microsoft work account</span>
            </div>
            <div className="info-item">
              <Shield size={16} />
              <span>Secure federated authentication</span>
            </div>
          </div>

          {/* Help Text */}
          <div className="auth-help">
            <p>
              Click the button above to sign in with your Microsoft work account. 
              You'll be redirected to Microsoft's authentication page and 
              then returned to the admin panel.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomLogin;
